import React, { useMemo } from 'react';
import Select, { StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useTimeFormat, formatHHmm } from '../../../util/timeFormat';

/**
 * Searchable time dropdown — the SAME react-select control used in
 * Settings → Calendar (business hours), so every hour picker across the app
 * (team working hours, calendar visible-hours) looks identical.
 *
 * I/O is canonical 24h "HH:mm" (or "" when empty); the visible LABELS follow
 * the business 12h/24h setting. Options are on a 30-minute grid; an off-grid
 * current value (e.g. "09:15") is preserved so editing never drops it.
 *
 * With {@link Props.allowCustom}, the user can TYPE any time (e.g. "13:51",
 * "1351", "9") and pick it — used for appointments where a slot can start at any
 * minute, not just the 30-min grid.
 */
interface TimeOption {
    value: string;
    label: string;
}

interface Props {
    /** "HH:mm" or "" */
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    /** Show an empty option (e.g. "no override"). */
    allowEmpty?: boolean;
    emptyLabel?: string;
    /** Allow typing an arbitrary time (HH:mm / HHmm / H) instead of only grid marks. */
    allowCustom?: boolean;
    /** Minutes between proposed options (the business "booking increment"). Default 30. */
    stepMinutes?: number;
    inputId?: string;
    'data-cy'?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Build a day-long grid of canonical "HH:mm" at `step` minutes (clamped 1..720). */
const buildGrid = (step: number): string[] => {
    const s = Math.min(720, Math.max(1, Math.round(step)));
    const out: string[] = [];
    for (let min = 0; min < 24 * 60; min += s) out.push(`${pad(Math.floor(min / 60))}:${pad(min % 60)}`);
    return out;
};

/** Parse free user input ("13:51", "1351", "135", "9") to canonical "HH:mm", or null. */
const parseTime = (raw: string): string | null => {
    if (!raw) return null;
    const s = raw.trim();
    let h: number | undefined;
    let m: number | undefined;
    const colon = s.match(/^(\d{1,2})\s*[:hH]\s*(\d{1,2})$/);
    if (colon) {
        h = Number(colon[1]);
        m = Number(colon[2]);
    } else {
        const digits = s.replace(/\D/g, '');
        if (digits.length === 0) return null;
        if (digits.length <= 2) { h = Number(digits); m = 0; }
        else if (digits.length === 3) { h = Number(digits.slice(0, 1)); m = Number(digits.slice(1)); }
        else if (digits.length === 4) { h = Number(digits.slice(0, 2)); m = Number(digits.slice(2)); }
        else return null;
    }
    if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return `${pad(h)}:${pad(m)}`;
};

// Colors come from the --gs-* design tokens (same as Settings → Calendar) so the
// control follows the light/dark theme automatically.
const styles: StylesConfig<TimeOption, false> = {
    control: (base, state) => ({
        ...base,
        minHeight: 38,
        // Fits "08:00"/"11:30 PM" even in the narrow team working-hours columns.
        minWidth: 72,
        backgroundColor: 'var(--gs-surface, #fff)',
        borderColor: state.isFocused
            ? 'var(--gs-primary, #7266e8)'
            : 'var(--gs-border-strong, #d7d7e2)',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(114, 102, 232, 0.15)' : 'none',
        '&:hover': { borderColor: 'var(--gs-primary, #7266e8)' },
    }),
    // Tighten the inner padding + drop the separator so the value isn't clipped
    // in compact cells (the working-hours table packs many columns).
    valueContainer: (base) => ({ ...base, padding: '0 4px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({
        ...base,
        maxHeight: '300px',
        minWidth: 110,
        backgroundColor: 'var(--gs-surface, #fff)',
        border: '1px solid var(--gs-border, #ececf4)',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    singleValue: (base) => ({ ...base, color: 'var(--gs-text-body, #1d1a33)' }),
    input: (base) => ({ ...base, color: 'var(--gs-text-body, #1d1a33)', margin: 0, padding: 0 }),
    placeholder: (base) => ({ ...base, color: 'var(--gs-text-soft, #888)' }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--gs-primary, #7266e8)'
            : state.isFocused
                ? 'var(--gs-primary-softer, #efedfd)'
                : 'var(--gs-surface, #fff)',
        color: state.isSelected ? '#fff' : 'var(--gs-text-body, #1d1a33)',
        cursor: 'pointer',
    }),
};

export default function TimeSelect({
    value, onChange, disabled, allowEmpty, emptyLabel = '--:--', allowCustom, stepMinutes, inputId, ...rest
}: Props) {
    const fmt = useTimeFormat();
    const v = (value || '').slice(0, 5);

    // Proposed options follow the business "booking increment" (e.g. 15 → 14:00,
    // 14:15, 14:30…). Falls back to 30 min when unset.
    const step = stepMinutes && stepMinutes > 0 ? Math.round(stepMinutes) : 30;

    const options = useMemo<TimeOption[]>(() => {
        const TIME_GRID = buildGrid(step);
        // Preserve an off-grid value (e.g. 09:15) by inserting it IN CHRONOLOGICAL
        // ORDER — canonical "HH:mm" sorts lexicographically == by time — so it never
        // jumps to the top of the list ("13:51" sits between 13:30 and 14:00).
        const grid = v && !TIME_GRID.includes(v) ? [...TIME_GRID, v].sort() : TIME_GRID;
        const opts = grid.map((t) => ({ value: t, label: formatHHmm(t, fmt) }));
        return allowEmpty ? [{ value: '', label: emptyLabel }, ...opts] : opts;
    }, [v, fmt, allowEmpty, emptyLabel, step]);

    const selected = options.find((o) => o.value === v) ?? (allowEmpty ? options[0] : null);

    // Scroll the selected option into view (react-select doesn't when portaled).
    const scrollSelectedIntoView = () => {
        setTimeout(() => {
            const el = document.querySelector(
                '.gs-select__menu-portal .gs-select__option--is-selected'
            );
            if (el) (el as HTMLElement).scrollIntoView({ block: 'center' });
        }, 0);
    };
    const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;
    const common = {
        inputId,
        classNamePrefix: 'gs-select' as const,
        value: selected,
        options,
        isSearchable: true,
        isDisabled: disabled,
        menuPortalTarget,
        onMenuOpen: scrollSelectedIntoView,
        styles,
        'data-cy': rest['data-cy'],
    };

    // react-select does NOT forward unknown props (data-cy never reaches the DOM):
    // expose it on a layout-neutral wrapper so e2e tests can target the control.
    const wrap = (node: React.ReactElement) => rest['data-cy']
        ? <span data-cy={rest['data-cy']} style={{ display: 'contents' }}>{node}</span>
        : node;

    if (allowCustom) {
        // Free entry: the user can type any time ("13:51", "1351", "9") and pick it.
        return wrap(
            <CreatableSelect<TimeOption>
                {...common}
                onChange={(opt) => onChange(opt ? opt.value : '')}
                createOptionPosition="first"
                // Offer "create" ONLY when the typed text parses to a real time that
                // ISN'T already an option — otherwise typing "00:00" duplicates the
                // grid entry. Match against the parsed value, not the raw text.
                isValidNewOption={(input) => {
                    const p = parseTime(input);
                    return !!p && !options.some((o) => o.value === p);
                }}
                // Also surface grid options while typing free digits ("1351" → 13:51):
                // match the option's canonical value, not just its formatted label.
                filterOption={(option, input) => {
                    if (!input) return true;
                    const p = parseTime(input);
                    if (p && option.value === p) return true;
                    return option.label.toLowerCase().includes(input.toLowerCase());
                }}
                formatCreateLabel={(input) => {
                    const parsed = parseTime(input);
                    return parsed ? formatHHmm(parsed, fmt) : input;
                }}
                getNewOptionData={(input, label) => ({
                    value: parseTime(input) ?? input,
                    label: label as string,
                })}
                onCreateOption={(input) => {
                    const parsed = parseTime(input);
                    if (parsed) onChange(parsed);
                }}
            />
        );
    }

    return wrap(
        <Select<TimeOption>
            {...common}
            onChange={(opt) => onChange(opt ? opt.value : '')}
        />
    );
}
